import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const FEATURED = [
  {
    slug: "sue-bird-monument",
    title: "Sue Bird Monument",
    client: "Muros × Seattle Storm",
    blurb:
      "Large-scale mural honoring a basketball legend — unveiled the day before her jersey retirement. Endorsed by Sue herself.",
    metrics: [
      { k: "15+", v: "Media Outlets" },
      { k: "2M+", v: "Social Reach" },
      { k: "National", v: "Press Tier" },
    ],
    img: "https://res.cloudinary.com/dxa827voe/images/w_2000,h_1285,c_scale/f_auto,q_auto/v1686613179/Muros_Storm_Original7_v2/Muros_Storm_Original7_v2.jpg",
  },
  {
    slug: "jose-bautista-bat-flip",
    title: "Bautista Bat Flip Mural",
    client: "Muros × Toronto Blue Jays",
    blurb:
      "Iconic mural commemorating baseball's most legendary moment. Viral across Reddit, Instagram, and international sports media.",
    metrics: [
      { k: "15K+", v: "Reddit Upvotes" },
      { k: "50K+", v: "IG Likes" },
      { k: "Int'l", v: "Coverage" },
    ],
    img: "https://muralroutes.ca/wp-content/uploads/2023/08/IMG_3338.jpeg",
  },
  {
    slug: "tanger-nashville-murals",
    title: "Tanger Nashville Murals",
    client: "Muros × Tanger Outlets",
    blurb:
      "13 artists, 13 original murals across 290,000 sq ft — one of the largest commercial mural programs in the country.",
    metrics: [
      { k: "13", v: "Murals" },
      { k: "290K", v: "Sq Ft" },
      { k: "Lead", v: "Marketer" },
    ],
    img: "https://res.cloudinary.com/dxa827voe/images/w_2000,h_1500,c_scale/f_auto,q_auto/v1698945550/DJI_0100-3/DJI_0100-3.jpg",
  },
  {
    slug: "titan-walls-2024",
    title: "Titan Walls 2024",
    client: "Muros × Titan America",
    blurb:
      "Large-scale mural initiative merging public art with strategic brand partnerships. Community-celebrated, press-backed.",
    metrics: [
      { k: "Multi-city", v: "Scale" },
      { k: "Press+Social", v: "Channels" },
      { k: "Cultural", v: "Format" },
    ],
    img: "https://cdn.crevado.com/artists/1287474/pages/5961018_original-5.jpg",
  },
  {
    slug: "choose-chicago-at-art-basel",
    title: "Choose Chicago at Art Basel",
    client: "Muros × Choose Chicago",
    blurb:
      "Positioning Chicago as a cultural destination to high-net-worth audiences at the world's premier art fair.",
    metrics: [
      { k: "Art Basel", v: "Event" },
      { k: "HNW", v: "Audience" },
      { k: "Destination", v: "Format" },
    ],
    img: "https://cdn.crevado.com/artists/1287474/pages/5960250_original-4.jpg",
  },
  {
    slug: "behind-the-brush",
    title: "Behind the Brush",
    client: "Muros Original Series",
    blurb:
      "Documentary-style original series profiling the artists behind the work — brand storytelling over sales.",
    metrics: [
      { k: "Original", v: "Series" },
      { k: "Storytelling", v: "Format" },
      { k: "Authority", v: "Outcome" },
    ],
    img: "https://cdn.crevado.com/artists/1287474/pages/5959690_original-2.png",
  },
  {
    slug: "revolution-cannabis",
    title: "Revolution Cannabis",
    client: "Revolution Global",
    blurb:
      "One of the most award-winning cannabis brands in history — top 5 High Times, 40+ Cup awards, 47,000% social growth.",
    metrics: [
      { k: "47,000%", v: "Social Growth" },
      { k: "Top 5", v: "High Times" },
      { k: "40+", v: "Cup Awards" },
    ],
    img: "https://cdn.crevado.com/artists/1287474/pages/4726534_original-2.jpg",
  },
  {
    slug: "chicago-addy-awards-2023",
    title: "JumpSeat Agency Leadership",
    client: "JumpSeat",
    blurb:
      "Led strategy and creative across a roster of brand partners. 100% client renewal across multi-year relationships.",
    metrics: [
      { k: "100%", v: "Retention" },
      { k: "Strategy", v: "+ Creative" },
      { k: "Multi-Client", v: "Scope" },
    ],
    img: "https://cdn.crevado.com/artists/1287474/pages/5971966_original-3.jpg",
  },
];

export default function Impact() {
  return (
    <section
      id="impact"
      data-testid="impact-section"
      className="px-4 md:px-8 lg:px-12 py-20 md:py-28 bg-[#111111] text-[#F2EFE9] hard-border-t hard-border-b"
    >
      <div className="mb-12 md:mb-16 max-w-6xl">
        <div className="font-mono-s text-[11px] tracking-[0.3em] uppercase text-[#F2EFE9]/60 mb-3">
          § 05 — Featured Impact
        </div>
        <h2 className="font-anton uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.92]">
          Work that
          <span className="font-cormorant italic text-[#D83127] font-semibold"> made </span>
          news.
        </h2>
        <p className="font-cormorant italic text-xl md:text-2xl mt-6 max-w-2xl text-[#F2EFE9]/80">
          Campaigns that earned national press, went viral across social, and shaped culture —
          not just slide decks.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-5 md:gap-6">
        {FEATURED.map((f, i) => (
          <motion.div
            key={f.slug}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: (i % 4) * 0.05 }}
            className="col-span-12 md:col-span-6 lg:col-span-3"
            data-testid={`impact-card-${i}`}
          >
            <Link
              to={`/project/${f.slug}`}
              className="group block bg-[#1a1a1a] border border-[#333] hover:border-[#D83127] transition-colors h-full"
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#111] border-b border-[#333]">
                <img
                  src={f.img}
                  alt={f.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                />
              </div>
              <div className="p-5">
                <div className="font-mono-s text-[10px] tracking-[0.25em] uppercase text-[#F2EFE9]/60 mb-2">
                  {f.client}
                </div>
                <div className="font-anton uppercase text-2xl md:text-3xl leading-tight group-hover:text-[#D83127] transition-colors">
                  {f.title}
                </div>
                <p className="font-cormorant italic text-base mt-2 text-[#F2EFE9]/85 leading-snug">
                  {f.blurb}
                </p>
                <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2">
                  {f.metrics.map((m, mi) => (
                    <li key={mi} className="flex flex-col">
                      <span className="font-anton text-lg leading-none">{m.k}</span>
                      <span className="font-mono-s text-[9px] uppercase tracking-[0.2em] text-[#F2EFE9]/55 mt-0.5">
                        {m.v}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 font-mono-s text-[10px] tracking-[0.3em] uppercase text-[#D83127] opacity-0 group-hover:opacity-100 transition-opacity">
                  Read Case Study →
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
